import React, { useMemo, useState } from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Badge,
  Box,
  Button,
  Checkbox,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@material-ui/core';
import { makeStyles, withStyles } from '@material-ui/core/styles';
import {
  Add as AddIcon,
  Edit as EditIcon,
  ExpandMore as ExpandMoreIcon,
  InfoOutlined as InfoOutlinedIcon,
} from '@material-ui/icons';
import classnames from 'classnames';
import PropTypes from 'prop-types';
import Currency from 'Components/Format/Currency';
import { currency, percent } from 'Helpers/Formats';
import AccountStatus from 'Components/UI/AccountStatus';
import { usePortfolioDispatchContext, usePortfolioStateContext } from 'Components/UI/Portfolio/PortfolioContext';
import { refreshPortfolioCollection } from 'Components/UI/Portfolio/PortfolioActions';
import ArrowUpwardIcon from '@material-ui/icons/ArrowUpward';
import ArrowDownwardIcon from '@material-ui/icons/ArrowDownward';
import { orderCollection } from 'Helpers/Utils';

const useStyles = makeStyles(theme => ({
  label: {
    marginBottom: theme.spacing(2),
  },
  total: {
    display: 'flex',
    flexFlow: 'row',
    marginLeft: theme.spacing(1),
    width: 'max-content',
  },
  totalLabel: {
    fontWeight: 'bold',
    color: theme.palette.facet.primaryBlue,
  },
  totalAmount: {
    marginLeft: theme.spacing(1),
  },
  info: {
    paddingLeft: theme.spacing(1),
    cursor: 'pointer',
  },
  darkRow: {
    backgroundColor: theme.palette.facet.lightGray,
  },
  balance: {
    color: theme.palette.secondary.main,
    fontSize: 26,
    letterSpacing: 0,
    fontWeight: theme.typography.fontWeightMedium,
  },
  badgeText: {
    flexGrow: 1,
  },
  check: {
    padding: 0,
  },
  return: {
    display: 'flex',
    flexFlow: 'row',
    marginLeft: theme.spacing(2),
  },
  percentReturnLabel: {
    fontWeight: 'bold',
    color: theme.palette.facet.primaryBlue,
  },
  returnAmount: {
    marginLeft: theme.spacing(1),
    fontWeight: theme.typography.fontWeightBold,
  },
  dollarReturnLabel: {
    fontWeight: 'bold',
    color: theme.palette.facet.primaryBlue,
    marginLeft: theme.spacing(2),
  },
}));

const StyledBadge = withStyles(() => ({
  badge: {
    right: -20,
    top: 13,
    padding: '0 4px',
  },
}))(Badge);

export function useHoldingsTotal(holdings) {
  const total = useMemo(() => {
    return holdings.reduce((acc, cur) => {
      const val = cur.attr('currentValue');
      if (
        Number.isNaN(val)
        || val === undefined
        || val === null
        || cur.attr('disabled') === 'true'
      ) return acc;
      return acc + Number(val);
    }, 0);
  }, [holdings]);

  return total;
}

const getNextDirection = (dir) => {
  if (!dir || dir === 'desc') {
    return 'asc';
  }

  return 'desc';
};

const SortableTH = ({
  name,
  makeOnSetSort,
  selectedAttr,
  direction,
  children: childrenProp,
  attr,
  ...rest
}) => {
  let Icon = null;
  if (attr === selectedAttr) {
    Icon = direction === 'asc' ? (
      <ArrowUpwardIcon fontSize="small" color="primary" />
    ) : (
      <ArrowDownwardIcon fontSize="small" color="primary" />
    );
  }
  return (
    <TableCell onClick={makeOnSetSort(attr)} {...rest}>
      <div
        role="button"
        style={{
          display: 'flex',
          cursor: 'pointer',
          paddingRight: !Icon ? '20px' : '',
        }}
      >
        {childrenProp || (
          <Typography variant="h6" color="primary" noWrap>
            {name}
          </Typography>
        )}
        {Icon}
      </div>
    </TableCell>
  );
};

SortableTH.defaultProps = {
  children: null,
  selectedAttr: '',
  direction: '',
};

SortableTH.propTypes = {
  attr: PropTypes.string.isRequired,
  name: PropTypes.string.isRequired,
  direction: PropTypes.string,
  selectedAttr: PropTypes.string,
  makeOnSetSort: PropTypes.func.isRequired,
  children: PropTypes.any,
};

export default function HoldingsAccordion({
  accountResource,
  onOpenModal,
  excludedColumns,
  setDisableTable,
  disableTable,
  hidePercentReturn,
}) {
  const dispatch = usePortfolioDispatchContext();
  const stateContext = usePortfolioStateContext();

  const classes = useStyles();
  const [[selectedAttr, sortDirection], setSortState] = useState([
    'currentValue',
    'desc',
  ]);

  const holdings = orderCollection(
    accountResource.attr('holdings'),
    selectedAttr,
    sortDirection,
  );

  const totalValue = useHoldingsTotal(holdings);

  const handleToggleHolding = async (holding) => {
    try {
      setDisableTable(true);
      const newValue = String(holding.attr('disabled') === 'false');
      holding.attr('disabled', newValue);
      await holding.submit();
      await refreshPortfolioCollection(dispatch, stateContext);
    } finally {
      setDisableTable(false);
    }
  };

  const makeOnSetSort = name => () => setSortState([
    name,
    getNextDirection(name === selectedAttr ? sortDirection : false),
  ]);

  const TABLE_DATA = {
    Enabled: {
      TH: (
        <SortableTH
          makeOnSetSort={makeOnSetSort}
          selectedAttr={selectedAttr}
          direction={sortDirection}
          name="Enabled"
          attr="disabled"
        />
      ),
      TD: holding => (
        <TableCell>
          <Checkbox
            aria-label="disable"
            size="small"
            onClick={() => handleToggleHolding(holding)}
            checked={holding.attr('disabled') === 'false'}
            disabled={disableTable}
            className={classes.check}
          />
        </TableCell>
      ),
    },
    Ticker: {
      TH: (
        <SortableTH
          name="Ticker"
          makeOnSetSort={makeOnSetSort}
          selectedAttr={selectedAttr}
          direction={sortDirection}
          attr="ticker"
        />
      ),
      TD: holding => (
        <TableCell>
          <a
            rel="noopener noreferrer"
            target="_blank"
            href={holding
              .relationship('research')
              ?.link('related')
              ?.url()}
          >
            <Typography variant="h6">{holding.attr('ticker')}</Typography>
          </a>
        </TableCell>
      ),
    },
    'Holding Name': {
      TH: (
        <SortableTH
          name="Holding Name"
          makeOnSetSort={makeOnSetSort}
          selectedAttr={selectedAttr}
          direction={sortDirection}
          attr="name"
        />
      ),
      TD: holding => (
        <TableCell>
          <Typography variant="h6">{holding.attr('name')}</Typography>
        </TableCell>
      ),
    },
    Classification: {
      TH: (
        <SortableTH
          name="Classification"
          makeOnSetSort={makeOnSetSort}
          selectedAttr={selectedAttr}
          direction={sortDirection}
          attr="tickerClassification"
        />
      ),
      TD: holding => (
        <TableCell>
          <Typography variant="h6">
            {holding.selectedOptions('tickerClassification', 'prompt')[0]}
          </Typography>
        </TableCell>
      ),
    },
    Quantity: {
      TH: (
        <SortableTH
          name="Quantity"
          makeOnSetSort={makeOnSetSort}
          selectedAttr={selectedAttr}
          direction={sortDirection}
          attr="quantity"
        />
      ),
      TD: holding => (
        <TableCell>
          <Typography variant="h6">{holding.attr('quantity')}</Typography>
        </TableCell>
      ),
    },
    'Current Price': {
      TH: (
        <SortableTH
          name="Current Price"
          makeOnSetSort={makeOnSetSort}
          selectedAttr={selectedAttr}
          direction={sortDirection}
          attr="currentPrice"
        />
      ),
      TD: holding => (
        <TableCell align="right">
          <Typography variant="h6">
            {currency(holding.attr('currentPrice'))}
          </Typography>
        </TableCell>
      ),
    },
    'Cost Basis': {
      TH: (
        <SortableTH
          name="Cost Basis"
          makeOnSetSort={makeOnSetSort}
          selectedAttr={selectedAttr}
          direction={sortDirection}
          attr="costBasis"
        >
          <Box display="flex">
            <Typography variant="h6" color="primary" noWrap>
              Cost Basis
            </Typography>
            <Tooltip
              title="Cost Basis may not be accurate from custodian."
              placement="top"
              arrow
            >
              <InfoOutlinedIcon
                fontSize="small"
                color="primary"
                className={classes.info}
              />
            </Tooltip>
          </Box>
        </SortableTH>
      ),
      TD: holding => (
        <TableCell>
          <Typography variant="h6">
            {currency(holding.attr('costBasis'))}
          </Typography>
        </TableCell>
      ),
    },
    'Expense Ratio': {
      TH: (
        <SortableTH
          name="Expense Ratio"
          makeOnSetSort={makeOnSetSort}
          selectedAttr={selectedAttr}
          direction={sortDirection}
          attr="expenseRatio"
        />
      ),
      TD: holding => (
        <TableCell>
          <Typography variant="h6">
            {percent(holding.attr('expenseRatio'))}
          </Typography>
        </TableCell>
      ),
    },
    'Current Value': {
      TH: (
        <SortableTH
          name="Current Value"
          makeOnSetSort={makeOnSetSort}
          selectedAttr={selectedAttr}
          direction={sortDirection}
          attr="currentValue"
        >
          <Typography variant="h6" color="primary" noWrap>
            {'Current Value'}
          </Typography>
        </SortableTH>
      ),
      TD: holding => (
        <TableCell>
          <Typography variant="h6">
            {currency(holding.attr('currentValue'))}
          </Typography>
        </TableCell>
      ),
    },
    GainPercent: {
      TH: (
        <SortableTH
          name="Gain Percent"
          makeOnSetSort={makeOnSetSort}
          selectedAttr={selectedAttr}
          direction={sortDirection}
          attr="percentGain"
        >
          <Typography variant="h6" color="primary" noWrap>
            {'Gain Percent'}
          </Typography>
        </SortableTH>
      ),
      TD: holding => (
        <TableCell>
          <Typography
            variant="h6"
            color={
              parseFloat(holding.attr('percentGain')) >= 0
                ? 'secondary'
                : 'error'
            }
          >
            {percent(holding.attr('percentGain'))}
          </Typography>
        </TableCell>
      ),
    },
    Gain: {
      TH: (
        <SortableTH
          name="Gain"
          makeOnSetSort={makeOnSetSort}
          selectedAttr={selectedAttr}
          direction={sortDirection}
          attr="dollarGain"
        />
      ),
      TD: holding => (
        <TableCell>
          <Typography
            variant="h6"
            color={
              parseFloat(holding.attr('dollarGain')) >= 0
                ? 'secondary'
                : 'error'
            }
          >
            {currency(holding.attr('dollarGain'))}
          </Typography>
        </TableCell>
      ),
    },
    Edit: {
      TH: (
        <TableCell align="right">
          <Typography variant="h6" color="primary" noWrap>
            {'Edit'}
          </Typography>
        </TableCell>
      ),
      TD: holding => (
        <TableCell align="right">
          <IconButton
            aria-label="edit"
            size="small"
            onClick={onOpenModal(
              accountResource.resourceId,
              holding.resourceId,
            )}
            style={{ height: 20, width: 20, padding: 0 }}
          >
            <EditIcon style={{ height: 20, width: 20 }} />
          </IconButton>
        </TableCell>
      ),
    },
  };

  const cols = Object.keys(TABLE_DATA).filter(
    k => !excludedColumns.includes(k),
  );
  const showIndictator = accountResource?.attr('accountAggregatorMetaId')
    || accountResource?.attr('isManaged');
  return (
    <Accordion
      square
      component={Box}
      defaultExpanded={
        accountResource
          .attr('holdings')
          .resources()
          .length() > 0
      }
    >
      <AccordionSummary
        expandIcon={<ExpandMoreIcon />}
        aria-controls="panel1a-content"
        id="panel1a-header"
      >
        <Box display="flex" width="100%">
          <div className={classes.badgeText}>
            {showIndictator && (
              <AccountStatus
                className={classes.accountLink}
                resource={accountResource}
                syncGood
                tooltip
              />
            )}
            <StyledBadge
              badgeContent={accountResource
                .attr('holdings')
                .resources()
                .length()}
              color="primary"
            >
              <Typography variant="h4">
                {accountResource.attr('name') || 'Unnamed Account'}
              </Typography>
            </StyledBadge>
          </div>
          <Typography className={classes.balance}>
            <Currency amount={totalValue} />
          </Typography>
        </Box>
      </AccordionSummary>
      <AccordionDetails>
        <Box
          display="flex"
          flexDirection="column"
          alignItems="flex-end"
          minWidth="100%"
        >
          <Button
            aria-label="add"
            size="small"
            className={classes.addButton}
            color="secondary"
            onClick={onOpenModal(accountResource.resourceId, null)}
          >
            <AddIcon />
            {'Add Holding'}
          </Button>
          <br />
          <TableContainer component={Paper}>
            <Table size="small">
              <TableHead>
                <TableRow>{cols.map(key => TABLE_DATA[key].TH)}</TableRow>
              </TableHead>
              <TableBody>
                {holdings.map((holding, index) => (
                  <TableRow
                    key={holding.resourceId}
                    className={classnames({ [classes.darkRow]: index % 2 })}
                  >
                    {cols.map(key => TABLE_DATA[key].TD(holding))}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            <div className={classes.total}>
              <Typography variant="h6" className={classes.totalLabel}>
                Total Value:
              </Typography>
              <Typography variant="h6" className={classes.totalAmount}>
                {currency(totalValue)}
              </Typography>
              {!hidePercentReturn && (
                <div className={classes.return}>
                  <Typography
                    variant="h6"
                    className={classes.percentReturnLabel}
                  >
                    % Return:
                  </Typography>
                  <Typography
                    variant="h6"
                    className={classes.returnAmount}
                    color={(() => {
                      if (!accountResource.attr('percentReturn')) {
                        return 'secondary';
                      }
                      return parseFloat(
                        accountResource.attr('percentReturn'),
                      ) >= 0
                        ? 'secondary'
                        : 'error';
                    })()}
                  >
                    {`${accountResource.attr('percentReturn')}%`}
                  </Typography>
                  <Typography
                    variant="h6"
                    className={classes.dollarReturnLabel}
                  >
                    Return:
                  </Typography>
                  <Typography
                    variant="h6"
                    className={classes.returnAmount}
                    color={(() => {
                      if (!accountResource.attr('dollarReturn')) {
                        return 'secondary';
                      }
                      return parseFloat(accountResource.attr('dollarReturn'))
                        >= 0
                        ? 'secondary'
                        : 'error';
                    })()}
                  >
                    {currency(accountResource.attr('dollarReturn'))}
                  </Typography>
                </div>
              )}
            </div>
          </TableContainer>
        </Box>
      </AccordionDetails>
    </Accordion>
  );
}

HoldingsAccordion.defaultProps = {
  excludedColumns: [],
  hidePercentReturn: false,
};

HoldingsAccordion.propTypes = {
  excludedColumns: PropTypes.array,
  accountResource: PropTypes.object.isRequired,
  onOpenModal: PropTypes.func.isRequired,
  disableTable: PropTypes.bool.isRequired,
  setDisableTable: PropTypes.func.isRequired,
  hidePercentReturn: PropTypes.bool,
};
